import { useState } from 'react';
import { useAgentMutations } from '../lib/queries';
import { qrSvg } from '../lib/qr';
import type { Agent } from '../data/types';
import { Modal } from './Agents';
import { errorText } from '../lib/errors';

/** What the counter QR encodes. The driver app resolves `/a/<code>`. */
const counterUrl = (code: string) => `https://ev.nexdecade.com/a/${code}`;

const esc = (v: string) => v.replace(/[&<>"]/g,
  (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] ?? c);

/**
 * One cash counter's Spark Agent setup: the QR it displays, its login, and
 * where it is.
 *
 * The QR is printed at error-correction H — a label taped to a counter gets
 * scuffed, and H still reads with 30% of it gone — and always black on white.
 */
export function AgentCounter({ agent, onClose }: { agent: Agent; onClose: () => void }) {
  const { issueLogin, updateCounter, reissueCode } = useAgentMutations();
  const [email, setEmail] = useState(agent.email ?? '');
  const [phone, setPhone] = useState(agent.phone ?? '');
  const [issued, setIssued] = useState<{ password: string; who: string } | null>(null);
  const [counterName, setCounterName] = useState(agent.counterName ?? agent.name);
  const [city, setCity] = useState(agent.city ?? '');
  const [address, setAddress] = useState(agent.address ?? '');
  const [lat, setLat] = useState(agent.lat == null ? '' : String(agent.lat));
  const [lng, setLng] = useState(agent.lng == null ? '' : String(agent.lng));
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmReissue, setConfirmReissue] = useState(false);
  /* Said beside the Reissue button rather than in `msg` at the foot of the
     dialog, which is a scroll away from where the button was pressed. */
  const [reissueError, setReissueError] = useState<string | null>(null);

  const code = agent.counterCode ?? null;
  const label = agent.counterName ?? agent.name;

  const print = () => {
    if (!code) return;
    const w = window.open('', '_blank', 'width=480,height=640');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>${esc(label)} · counter QR</title>
<style>body{font:15px/1.4 -apple-system,Segoe UI,Roboto,sans-serif;text-align:center;padding:32px;color:#000}
h1{font-size:22px;margin:16px 0 4px}p{margin:4px 0}.c{font:600 20px monospace;letter-spacing:3px;margin-top:10px}</style>
</head><body>
<p><b>⚡ Spark EV</b> · Cash top-up</p>
<div>${qrSvg(counterUrl(code), 320)}</div>
<h1>${esc(label)}</h1>
<p>Scan with the Spark EV app to pay cash here.</p>
<p class="c">${esc(code)}</p>
<script>window.onload=()=>{window.print()}</script>
</body></html>`);
    w.document.close();
  };

  const download = () => {
    if (!code) return;
    const blob = new Blob([qrSvg(counterUrl(code), 600)], { type: 'image/svg+xml' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `counter-qr-${code}.svg`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const issue = () => {
    setMsg(null);
    issueLogin.mutate(
      { id: agent.id, ...(email.trim() ? { email: email.trim() } : {}), ...(phone.trim() ? { phone: phone.trim() } : {}) },
      {
        onSuccess: (r) => setIssued({ password: r.password, who: r.email ?? r.phone ?? '' }),
        onError: (e: Error) => setMsg(
          e.message === 'contact_taken' ? 'That email or phone already belongs to another counter.'
            : e.message === 'bad_phone' ? 'Phone looks like 0911 234 567.'
              : e.message === 'bad_email' ? 'That email address is not valid.'
                : e.message === 'email_or_phone_required' ? 'Give an email, a phone, or both.'
                  : errorText(e)),
      },
    );
  };

  /* The refusals this dialog can meet, in words an operator can act on. */
  const switch_ = (e: Error) => {
    const code = e.message;
    if (code === 'bad_coordinates') return 'Latitude and longitude look wrong.';
    if (code === 'bad_email') return 'That email address does not look right.';
    if (code === 'bad_phone') return 'That phone number does not look right.';
    if (code === 'contact_taken') {
      return 'Another counter already uses that email or phone. Each agent signs in with its own.';
    }
    return errorText(e);
  };

  const save = () => {
    setMsg(null);
    const num = (v: string) => (v.trim() === '' ? null : Number(v));
    updateCounter.mutate(
      /* Email and phone save here now. They used to be settable only by
          issuing a login, which resets the password and signs the counter out —
          so correcting a typo cost the agent their credentials. */
      { id: agent.id, counterName: counterName.trim() || null, city: city.trim() || null,
        address: address.trim() || null, lat: num(lat), lng: num(lng),
        email: email.trim() || null, phone: phone.trim() || null },
      {
        onSuccess: () => setMsg('Counter details saved.'),
        onError: (e: Error) => setMsg(switch_(e)),
      },
    );
  };

  return (
    <Modal label={`${label} counter`} onClose={onClose}>
      <div style={{ maxHeight: '80vh', overflowY: 'auto' }}>
        <div className="li-title" style={{ fontSize: 15 }}>{label}</div>
        <div className="li-sub" style={{ marginBottom: 12 }}>{agent.area}{agent.city ? ` · ${agent.city}` : ''}</div>

        {/* ------------------------------------------------------ QR ---- */}
        <div className="li-sub" style={{ fontWeight: 600 }}>Counter QR</div>
        {code ? (
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', margin: '8px 0 14px' }}>
            <div style={{ background: '#fff', padding: 10, borderRadius: 10, lineHeight: 0 }}
                 dangerouslySetInnerHTML={{ __html: qrSvg(counterUrl(code), 150) }} />
            <div style={{ flex: '1 1 160px', minWidth: 0 }}>
              <div className="mono" style={{ fontSize: 16, fontWeight: 700, letterSpacing: 2 }}>{code}</div>
              <div className="li-sub" style={{ wordBreak: 'break-all' }}>{counterUrl(code)}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                <button className="btn sm" onClick={print}>Print label</button>
                <button className="btn sm ghost" onClick={download}>Download SVG</button>
                <button className="btn sm ghost" onClick={() => setConfirmReissue(true)}>Reissue</button>
              </div>
              {confirmReissue && (
                <div className="note" style={{ marginTop: 8 }}>
                  The printed label stops working at once and a new one must go up.{' '}
                  <button className="btn sm" disabled={reissueCode.isPending}
                          onClick={() => {
                            setReissueError(null);
                            reissueCode.mutate(agent.id, {
                              onSuccess: () => setConfirmReissue(false),
                              onError: (e: Error) => setReissueError(errorText(e)),
                            });
                          }}>
                    {reissueCode.isPending ? 'Reissuing…' : 'Reissue now'}
                  </button>{' '}
                  <button className="btn sm ghost" onClick={() => setConfirmReissue(false)}>Keep it</button>
                  {reissueError && (
                    <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginTop: 6 }}>
                      {reissueError}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="note" style={{ margin: '8px 0 14px' }}>
            No code yet. Issuing a login below creates the counter's QR.
          </div>
        )}

        {/* --------------------------------------------------- login ---- */}
        <div className="li-sub" style={{ fontWeight: 600 }}>Spark Agent login</div>
        <div className="li-sub" style={{ marginBottom: 6 }}>
          {agent.hasLogin
            ? <>Issued{agent.lastLoginAt ? ` · last sign-in ${new Date(agent.lastLoginAt).toLocaleString()}` : ' · never signed in'}.
                Issuing again replaces the password and signs every device out.</>
            : <>The agent signs in to Spark Agent with this email or phone.</>}
        </div>
        {issued ? (
          <div className="note" style={{ marginBottom: 14 }}>
            <b>Password for {issued.who}:</b>
            <div className="mono" style={{ fontSize: 18, fontWeight: 700, margin: '6px 0', userSelect: 'all' }}>
              {issued.password}
            </div>
            Shown once — it is not stored and cannot be read back. Hand it to the agent;
            Spark Agent makes them choose their own before they can take cash.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 6, marginBottom: 14 }}>
            <input className="field" placeholder="agent@example.com" value={email}
                   onChange={(e) => setEmail(e.target.value)} aria-label="Agent email" />
            <input className="field mono" placeholder="0911 234 567" value={phone}
                   onChange={(e) => setPhone(e.target.value)} aria-label="Agent phone" />
            <button className="btn sm" disabled={issueLogin.isPending} onClick={issue}>
              {agent.hasLogin ? 'Issue a new password' : 'Issue login'}
            </button>
          </div>
        )}

        {/* ------------------------------------------------ location ---- */}
        <div className="li-sub" style={{ fontWeight: 600 }}>Where drivers find it</div>
        <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
          <input className="field" placeholder="Counter name" value={counterName}
                 onChange={(e) => setCounterName(e.target.value)} aria-label="Counter name" />
          <input className="field" placeholder="City" value={city}
                 onChange={(e) => setCity(e.target.value)} aria-label="City" />
          <input className="field" placeholder="Address or landmark" value={address}
                 onChange={(e) => setAddress(e.target.value)} aria-label="Address" />
          <div className="li-sub faint" style={{ marginTop: 4 }}>
            Sign-in contact. Changing the email marks it unconfirmed again — a
            password reset goes there, so it has to be an address they can read.
          </div>
          <input className="field" placeholder="agent@example.com" value={email}
                 onChange={(e) => setEmail(e.target.value)} aria-label="Sign-in email" />
          <input className="field" placeholder="0911 234 567" value={phone}
                 onChange={(e) => setPhone(e.target.value)} aria-label="Sign-in phone" />
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="field mono" placeholder="Latitude" value={lat} inputMode="decimal"
                   onChange={(e) => setLat(e.target.value)} aria-label="Latitude" style={{ flex: 1 }} />
            <input className="field mono" placeholder="Longitude" value={lng} inputMode="decimal"
                   onChange={(e) => setLng(e.target.value)} aria-label="Longitude" style={{ flex: 1 }} />
          </div>
          <button className="btn sm ghost" disabled={updateCounter.isPending} onClick={save}>Save details</button>
        </div>

        {msg && <div className="li-sub" style={{ marginTop: 10 }}>{msg}</div>}

        <button className="btn ghost" style={{ width: '100%', marginTop: 14 }} onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}
